/**
 * "Photo courtesy of …" name suggestions for an article.
 *
 * Moved out of pages/ArticlePage.jsx on 2026-10-12 so the body-image
 * legend editor (components/ArticleBodyWithImages.jsx) pre-fills the
 * same name the featured image does. Unchanged logic.
 *
 * Pure and framework-free — node --test frontend/scripts/courtesyNames.test.mjs
 */

// Mirrors backend `routes/utils.COURTESY_PREFIX`. Trailing space is
// intentional. Also mirrored in StoryCardPanel.jsx.
export const COURTESY_PREFIX = "Photo courtesy of ";

// Outlet strings that carry no editorial meaning — never used as a
// courtesy name. Same list as backend `source_note._JUNK_OUTLETS`.
const JUNK_OUTLETS = new Set(["google news", "news.google.com", ""]);

// Hostname of `url`, lowercased, scheme and path removed, `www.` KEPT.
// Port of backend `source_note._host` EXCEPT for the `www.` handling:
// the backend strips it because it only ever uses the host for
// matching, whereas here the host can end up as published credit text
// and we want it to read as the site's real address — "www.xyz.com".
function legendHostOf(url) {
  if (!url) return "";
  let u = String(url).trim();
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;
  try {
    return new URL(u).hostname.toLowerCase();
  } catch {
    return "";
  }
}

// Host a configured property source points at. Port of backend
// `source_note._configured_source_host`: rss / website_scrape use the
// feed or page URL, site_filter uses the domain inside "site:example.com".
function configuredSourceHost(src) {
  let value = String(src?.value || "").trim();
  if (!value) return "";
  if (String(src?.type || "rss").trim() === "site_filter") {
    const m = value.match(/site:\s*([^\s]+)/i);
    value = m ? m[1] : value;
  }
  return legendHostOf(value);
}

// `www.` is stripped on BOTH sides here so matching is unaffected by
// the display-side decision above. Subdomains of a configured source
// still match, so noticias.example.mx resolves to the label set for
// example.mx.
function hostMatches(articleHost, sourceHost) {
  if (!articleHost || !sourceHost) return false;
  const a = articleHost.replace(/^www\./, "");
  const b = sourceHost.replace(/^www\./, "");
  return a === b || a.endsWith("." + b);
}

// Best display name for one source. Port of backend
// `source_note.resolve_source_name`, same three-rung ladder:
//   1. The name the editor gave this source in Settings → News sources.
//   2. The outlet the research pipeline recorded on the article.
//   3. The site's main hostname, e.g. "www.xyz.com" — used when the
//      photo came from somewhere we don't have configured at all.
// Returns "" for an unparseable URL so callers can skip the entry.
function resolveSourceName(configuredSources, item) {
  const articleHost = legendHostOf(item?.url);
  for (const src of configuredSources || []) {
    const name = String(src?.name || "").trim();
    if (!name) continue;
    if (hostMatches(articleHost, configuredSourceHost(src))) return name;
  }
  const outlet = String(item?.outlet || "").trim();
  if (outlet && !JUNK_OUTLETS.has(outlet.toLowerCase())) return outlet;
  return articleHost;
}

// Suggested "Photo courtesy of …" names for this article, deduped,
// order preserved. Offered via <datalist>, NOT a closed <select> —
// photos frequently come from a government page, PR contact or
// Facebook post that was never a research source, so the editor must
// always be able to type a name that isn't in this list.
export function sourceNameSuggestions(article, configuredSources) {
  const items =
    article.input_sources && article.input_sources.length > 0
      ? article.input_sources
      : (article.sources_used || []).map((u) => ({ url: u, outlet: "" }));
  const out = [];
  const seen = new Set();
  items.forEach((it) => {
    const name = resolveSourceName(configuredSources, it);
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(name);
  });
  return out;
}

// Name to pre-fill when the legend switches to `nextLegend`, or "" to
// leave the text alone. Only "courtesy" pre-fills, only when nothing
// has been typed yet, and only when there is a suggestion.
export function courtesyPrefill(nextLegend, currentText, names) {
  if (nextLegend !== "courtesy") return "";
  if (String(currentText || "").trim()) return "";
  return (names && names[0]) || "";
}
